import {
  ProductTagPlacement,
  ThumbnailShapeEnum,
  WidgetSizeEnum,
} from '@core/types'
import { embedFeedRender } from '@embed/embedFeedRender'

import { FWNElement } from './bases/FWNElement'

/**
 * Base class for embed feed elements with visibility-based lazy rendering.
 * Uses `lazy="true"` HTML attribute inherited from FWNElement to opt-in.
 */
class EmbedFeedElementBase extends FWNElement {
  #isRendered = false

  /**
   * Root margin for the IntersectionObserver.
   * Widget close to the bottom of the page will be rendered.
   */
  protected override visibilityRootMargin = '100px'

  /**
   * In order to make visibility-based lazy rendering work, we need
   * to override the performRender method of FWNElement and do nothing.
   */
  protected override performRender(): void {
    // noop
  }

  /**
   * Invoked when the component is visible or invisible.
   * @param isVisible - Whether the component is visible.
   */
  protected override onVisibilityChange(isVisible: boolean): void {
    if (isVisible && !this.#isRendered) {
      this.initializeExperiments()
      this.render()
      this.#isRendered = true
    }
  }

  /**
   * Re-render when attributes change, but only after the initial render has occurred.
   * This ensures lazy rendering still works (no render before visible),
   * while attribute updates (e.g. playlist) are reflected once rendered.
   */
  protected override onAttributeChange(): void {
    if (this.#isRendered) {
      this.render()
    }
  }
}

class GridElement extends EmbedFeedElementBase {
  override render(): void {
    if (this.shadowRoot) {
      embedFeedRender({
        mode: 'grid',
        widgetParams: this.getAttributes(),
        widgetId: this.widgetId,
        target: this.shadowRoot,
      })
    }
  }
}

class CarouselElement extends EmbedFeedElementBase {
  override render(): void {
    if (this.shadowRoot) {
      embedFeedRender({
        mode: 'row',
        widgetParams: this.getAttributes(),
        widgetId: this.widgetId,
        target: this.shadowRoot,
      })
    }
  }
}

class EmbedFeedElement extends EmbedFeedElementBase {
  override render(): void {
    if (this.shadowRoot) {
      embedFeedRender({
        mode: 'row',
        widgetParams: this.getAttributes(),
        widgetId: this.widgetId,
        target: this.shadowRoot,
      })
    }
  }
}

class PlayerDeckElement extends EmbedFeedElementBase {
  override render(): void {
    if (this.shadowRoot) {
      const attributes = this.getAttributes()
      const isGridMode = attributes.mode === 'grid'

      embedFeedRender({
        mode: isGridMode ? 'grid' : 'row',
        widgetParams: {
          // [CS-10225] the order here matters, we are setting captions: false
          // as the default for player deck but allowing users to override it
          captions: false,
          // Default to responsive thumbnails for the grid variant so the
          // existing responsiveColumnsForWidth() logic in Grid.tsx engages.
          ...(isGridMode ? { responsive_thumbnails: true } : {}),
          ...attributes,
          player_deck_layout: true,
          product_tag_placement: ProductTagPlacement.FALSE,
        },
        widgetId: this.widgetId,
        target: this.shadowRoot,
      })
    }
  }
}

class StoriesElement extends EmbedFeedElementBase {
  override render(): void {
    if (this.shadowRoot) {
      embedFeedRender({
        mode: 'row',
        widgetParams: {
          // default to small thumbs
          size: WidgetSizeEnum.SMALL,
          thumbnail_shape: ThumbnailShapeEnum.CIRCLE,
          ...this.getAttributes(),
          captions: false,
          product_tag_placement: ProductTagPlacement.FALSE,
        },
        widgetId: this.widgetId,
        target: this.shadowRoot,
      })
    }
  }
}

declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'fw-carousel': Record<string, string>
      'fw-grid': Record<string, string>
      'fw-player-deck': Record<string, string>
      'fw-stories': Record<string, string>
      'fw-embed-feed': Record<string, string>
    }
  }
}

const elements = new Map<string, typeof FWNElement>([
  ['fw-embed-feed', EmbedFeedElement],
  ['fw-carousel', CarouselElement],
  ['fw-grid', GridElement],
  ['fw-player-deck', PlayerDeckElement],
  ['fw-stories', StoriesElement],
])

elements.forEach((elementClass, elementName) => {
  if (!customElements.get(elementName)) {
    customElements.define(elementName, elementClass)
  }
})
